import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { 
  Sparkles, 
  Brain, 
  Heart, 
  Zap,
  ChevronLeft,
  ChevronRight 
} from 'lucide-react';

function App() {
  const [currentQuote, setCurrentQuote] = useState(0);
  const sectionsRef = useRef([]);

  const quotes = [
    {
      text: "Talent wins games, but teamwork and intelligence win championships.",
      author: "Michael Jordan"
    },
    {
      text: "Individual commitment to a group effort - that's what makes a team work.",
      author: "Vince Lombardi"
    },
    {
      text: "Great things in business are never done by one person. They're done by a team of people.",
      author: "Steve Jobs"
    }
  ];

  const scrollToSection = (index) => {
    sectionsRef.current[index]?.scrollIntoView({ behavior: 'smooth' });
  };

  const nextQuote = () => {
    setCurrentQuote((prev) => (prev + 1) % quotes.length);
  };

  const prevQuote = () => {
    setCurrentQuote((prev) => (prev - 1 + quotes.length) % quotes.length);
  };

  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const currentSection = Math.round(scrollPosition / windowHeight);
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation dots component
  const NavigationDots = () => (
    <div className="fixed right-8 top-1/2 transform -translate-y-1/2 flex flex-col gap-4 z-50">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <button
          key={index}
          onClick={() => scrollToSection(index)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${
            activeSection === index 
              ? 'bg-blue-500 scale-125' 
              : 'bg-gray-700 hover:bg-blue-400'
          } shadow-lg`}
        />
      ))}
    </div>
  );

  return (
    <div className="relative">
      <NavigationDots />

      {/* Hero Section */}
      <section 
        ref={el => sectionsRef.current[0] = el}
        className="min-h-screen relative flex items-center justify-center overflow-hidden snap-start bg-gradient-to-br from-gray-900 via-blue-900 to-black"
      >
        {/* Hero Content */}
        <div className="relative z-20 text-center px-4">
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6">
            WHAT MAKES A
            <span className="text-blue-500"> TEAM </span>
            SUCCESSFUL?
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Discover how great teams shape success, one collaboration at a time.
          </p>
          <button 
            onClick={() => scrollToSection(1)} 
            className="mt-8 px-8 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Start Your Journey
          </button>
        </div>
      </section>

      {/* Origin Section */}
      <section 
        ref={el => sectionsRef.current[1] = el}
        className="min-h-screen bg-gray-100 flex items-center snap-start"
      >
        <div className="max-w-6xl mx-auto px-4 py-20">
          <h2 className="text-4xl md:text-6xl font-bold text-blue-600 mb-4">
            The Power of Teamwork
          </h2>
          <h3 className="text-2xl text-gray-700 mb-8">
            From Individual Contributors to Collective Success
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <p className="text-gray-700 mb-6">
                The concept of teamwork has been fundamental to human progress throughout history.
                From ancient civilizations building monuments to modern organizations achieving
                breakthroughs, teams have been at the heart of every major accomplishment.
              </p>
              <p className="text-gray-700">
                In today's complex world, effective teamwork has become more crucial than ever.
                It's the cornerstone of innovation, efficiency, and sustainable success in any
                endeavor.
              </p>
            </div>
            <div>
              <img 
                src={require('./img.png')} 
                alt="Team Collaboration"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Definitions Section with Quote Navigation */}
      <section 
        ref={el => sectionsRef.current[2] = el}
        className="min-h-screen bg-white flex items-center snap-start"
      >
        <div className="max-w-6xl mx-auto px-4 py-20">
          <h2 className="text-4xl md:text-6xl font-bold text-center mb-16 text-gray-900">
            Perspectives on Teamwork
          </h2>
          <div className="relative max-w-4xl mx-auto">
            <button 
              onClick={prevQuote}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <div className="text-center px-16">
              <p className="text-xl md:text-2xl italic mb-4 transition-all duration-500">
                {quotes[currentQuote].text}
              </p>
              <p className="text-gray-600">— {quotes[currentQuote].author}</p>
            </div>

            <button 
              onClick={nextQuote}
              className="absolute right-0 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

{/* Team Development Section */}
<section 
  ref={el => sectionsRef.current[4] = el}
  className="min-h-screen bg-gray-50 flex items-center snap-start"
>
  <div className="max-w-6xl mx-auto px-4 py-20">
    <h2 className="text-4xl md:text-6xl font-bold text-blue-600 mb-4">
      Team Development Stages
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <img 
          src={require('./forming.png')} 
          alt="Team Forming"
          className="w-full h-48 object-cover rounded-lg mb-4"
        />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Forming</h3>
        <p className="text-gray-600">Team members get to know each other and understand their roles within the team structure.</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <img 
          src={require('./storming.png')} 
          alt="Team Storming"
          className="w-full h-48 object-cover rounded-lg mb-4"
        />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Storming</h3>
        <p className="text-gray-600">Different ideas compete for consideration, team addresses issues and finds solutions.</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <img 
          src={require('./performing.png')} 
          alt="Team Performing"
          className="w-full h-48 object-cover rounded-lg mb-4"
        />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Performing</h3>
        <p className="text-gray-600">Team reaches optimal performance level, working efficiently towards common goals.</p>
      </div>
    </div>
  </div>
</section>

{/* Future of Teams Section */}
<section 
  ref={el => sectionsRef.current[5] = el}
  className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white flex items-center snap-start"
>
  <div className="max-w-6xl mx-auto px-4 py-20">
    <h2 className="text-4xl md:text-6xl font-bold text-center mb-16">
      The Future of Team Collaboration
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div className="space-y-6">
        <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
          <h3 className="text-xl font-bold mb-3">Virtual Reality Meetings</h3>
          <p className="text-gray-300">Teams will collaborate in immersive virtual spaces, breaking down physical barriers.</p>
          <img 
            src={require('./vr-meeting.jpg')} 
            alt="VR Meeting"
            className="w-full h-48 object-cover rounded-lg mt-4"
          />
        </div>
        <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
          <h3 className="text-xl font-bold mb-3">AI-Powered Team Analytics</h3>
          <p className="text-gray-300">Artificial intelligence will provide insights into team dynamics and performance.</p>
          <img 
            src={require('./ai-analytics.jpeg')} 
            alt="AI Analytics"
            className="w-full h-48 object-cover rounded-lg mt-4"
          />
        </div>
      </div>
      <div className="flex items-center">
        <div className="bg-white/10 backdrop-blur p-8 rounded-xl">
          <h3 className="text-2xl font-bold mb-4">2025 and Beyond</h3>
          <p className="text-gray-300 mb-6">
            The future of teamwork will be characterized by seamless integration of technology
            and human collaboration. Teams will leverage advanced tools while maintaining
            the essential human elements that drive innovation and success.
          </p>
          <img 
            src={require('./future-team.jpg')} 
            alt="Future of Teams"
            className="w-full rounded-lg shadow-xl"
          />
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Types Section */}
      <section 
        ref={el => sectionsRef.current[3] = el}
        className="min-h-screen bg-gradient-to-br from-blue-900 to-black text-white flex items-center snap-start"
      >
        <div className="max-w-6xl mx-auto px-4 py-20">
          <h2 className="text-4xl md:text-6xl font-bold text-center mb-16">
            Elements of Great Teams
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Brain className="w-8 h-8" />,
                title: "Clear Communication",
                description: "The foundation of effective teamwork through open and honest dialogue."
              },
              {
                icon: <Heart className="w-8 h-8" />,
                title: "Trust & Support",
                description: "Building strong relationships and reliability among team members."
              },
              {
                icon: <Zap className="w-8 h-8" />,
                title: "Shared Vision",
                description: "Aligning team goals and objectives for collective success."
              },
              {
                icon: <Sparkles className="w-8 h-8" />,
                title: "Adaptability",
                description: "Flexible response to challenges and changing circumstances."
              }
            ].map((type, index) => (
              <div key={index} className="bg-gray-800/80 backdrop-blur p-8 rounded-xl">
                <div className="flex items-center gap-4 mb-4">
                  {type.icon}
                  <h3 className="text-xl font-bold">{type.title}</h3>
                </div>
                <p className="text-gray-300">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;